import { omit, uniq } from 'lodash-es'
import type { Address, AssetAddress } from './types.ts'
import { addressAreaItems } from './utils.ts'

function joinAddressParts(
  parts: (string | null | undefined | false)[],
  separator: string,
  trimShortStrings = true
): string {
  return uniq(
    parts
      .map((item) => {
        if (typeof item !== 'string') {
          return ''
        }

        const trimmed = item.trim()

        if (trimmed.length <= 2 && !/^\d+$/.test(trimmed) && trimShortStrings) {
          return ''
        }

        return trimmed
      })
      .filter((item) => !!item)
  ).join(separator)
}

export function createAddressAreaNameString(address?: Address | null): string {
  if (!address) {
    return ''
  }

  return joinAddressParts(
    [address.postal_code, address.city || address.municipality || address.region],
    ' '
  )
}

export function createAreaIdentifierString(address?: Address | null): string {
  if (!address) {
    return ''
  }

  return joinAddressParts(
    [address.postal_code, address.city || address.municipality || address.region, address.country],
    ', '
  )
}

export function formatAddressStreet(address: AssetAddress): string {
  return joinAddressParts([address.street, address.street_number, address.unit_number], ' ', false)
}

export function shortAddressString(address: AssetAddress | null): string {
  if (!address) {
    return ''
  }

  const fullStreet = formatAddressStreet(address)

  const addressWithFullStreet: AssetAddress = {
    ...omit(address, 'street_number', 'unit_number'),
    unit_number: '',
    street_number: '',
    street: fullStreet,
  }

  const priorityOrder: (keyof AssetAddress)[] = [
    'street',
    'postal_code',
    'city',
    'municipality',
    'region',
    'state',
    'country',
  ]

  const presentItems = addressAreaItems(addressWithFullStreet, priorityOrder)

  if (presentItems.length === 0) {
    return ''
  }

  return presentItems.reduce((result, item, index, array) => {
    if (index === array.length - 1) {
      // Last item, no comma
      return result + item
    }

    if (['street', 'city', 'state'].includes(priorityOrder[index])) {
      // Add comma after street, city, or state
      return `${result + item}, `
    }

    // For other items, add space
    return `${result + item} `
  }, '')
}

export function createAddressTitle(address?: AssetAddress | null, sparse = true) {
  if (!address) {
    return ''
  }

  const addressRow = joinAddressParts(
    [address.street, !sparse && address.street_number, !sparse && (address.unit_number || '')],
    ' '
  )

  return addressRow
}
